import { PaymentIntent } from '@stripe/stripe-js'
import { addDays, addMonths, addWeeks, addYears } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import localizeCurrency from '../helpers/localizeCurrency'
import localizeDate from '../helpers/localizeDate'
import { Plan, StripePrice } from '../types'

interface OrderSummaryProps {
  fullPrice: number | undefined
  fullPriceCurrency: string | undefined
  paymentIntent: PaymentIntent | undefined
  plan: Plan
  stripePrice: StripePrice | undefined
  stripePriceFull: StripePrice | undefined
  stripePriceIntro: StripePrice | undefined | null
  trialPeriodDays?: number
}

export default function OrderSummary({
  fullPrice,
  fullPriceCurrency,
  paymentIntent,
  plan,
  stripePrice,
  stripePriceFull,
  stripePriceIntro,
  trialPeriodDays,
}: OrderSummaryProps) {
  const { t } = useTranslation()

  const isOneTime = plan === 'one-time'
  const interval = stripePrice?.recurring?.interval
  const intervalCount = stripePrice?.recurring?.interval_count ?? 1

  const renewalDate = useMemo(() => {
    if (isOneTime) return null
    const now = new Date()
    if (trialPeriodDays) return addDays(now, trialPeriodDays)
    if (interval === 'week') return addWeeks(now, intervalCount)
    if (interval === 'month') return addMonths(now, intervalCount)
    if (interval === 'year') return addYears(now, intervalCount)
    return addWeeks(now, 1)
  }, [interval, intervalCount, isOneTime, trialPeriodDays])

  const capitalizeString = (str: string | undefined) => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">
        {t('pages.checkout.payment.orderSummary.heading')}
      </h2>

      <div className="border-primary/20 bg-primary/5 space-y-4 rounded-3xl border p-5">
        <div className="space-y-2 text-sm">
          <p className="text-primary font-bold">
            {t('pages.checkout.payment.orderSummary.durationHeadline')}
          </p>

          <p className="text-custom-light-gray">
            {trialPeriodDays
              ? t('pages.checkout.payment.orderSummary.durationTrial', {
                  count: trialPeriodDays,
                })
              : isOneTime
                ? t('pages.checkout.payment.orderSummary.durationOneTime')
                : t(
                    `pages.checkout.payment.orderSummary.duration${capitalizeString(interval) || 'Week'}`,
                    {
                      count: intervalCount,
                    },
                  )}
          </p>
        </div>

        {isOneTime ? (
          <div className="space-y-2 text-sm">
            <p className="text-primary font-bold">
              {t('pages.checkout.payment.orderSummary.renewalHeadline')}
            </p>

            <p className="text-custom-light-gray">
              {t('pages.checkout.payment.orderSummary.noRenewal')}
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-primary font-bold">
              {t('pages.checkout.payment.orderSummary.renewalHeadline')}
            </p>

            {renewalDate && (
              <p className="text-custom-light-gray">
                {localizeDate(renewalDate)}
              </p>
            )}

            <p className="text-custom-light-gray">
              {trialPeriodDays
                ? t('pages.checkout.payment.orderSummary.trialRenewal', {
                    price: localizeCurrency({
                      amountInCents: fullPrice,
                      currency: fullPriceCurrency,
                    }),
                  })
                : t('pages.checkout.payment.orderSummary.renewalDisclaimer')}
            </p>
          </div>
        )}

        {trialPeriodDays && (
          <div className="border-primary/20 border-t pt-4">
            <p className="text-custom-black font-bold">
              {t('pages.checkout.payment.orderSummary.dueTodayFree', {
                price: localizeCurrency({
                  amountInCents: 0,
                  currency: fullPriceCurrency,
                }),
              })}
            </p>
          </div>
        )}

        {paymentIntent && (
          <div className="border-primary/20 space-y-1 border-t pt-4">
            {isOneTime ? (
              <p className="text-custom-black font-bold">
                {t('pages.checkout.payment.oneTimePayment', {
                  price: localizeCurrency({
                    amountInCents: paymentIntent.amount,
                    currency: paymentIntent.currency,
                  }),
                })}
              </p>
            ) : stripePriceIntro ? (
              <>
                <p className="text-custom-light-gray text-sm line-through">
                  {t(
                    `pages.checkout.payment.this${capitalizeString(stripePriceFull?.recurring?.interval)}`,
                    {
                      count: stripePriceFull?.recurring?.interval_count,
                      price: localizeCurrency({
                        amountInCents: fullPrice,
                        currency: fullPriceCurrency,
                      }),
                    },
                  )}
                </p>

                <p className="text-custom-black font-bold">
                  {t(
                    `pages.checkout.payment.first${capitalizeString(stripePriceIntro?.recurring?.interval)}SpecialOffer`,
                    {
                      count: stripePriceIntro?.recurring?.interval_count,
                      price: localizeCurrency({
                        amountInCents: paymentIntent.amount,
                        currency: paymentIntent.currency,
                      }),
                    },
                  )}
                </p>
              </>
            ) : (
              <p className="text-custom-black font-bold">
                {t(
                  `pages.checkout.payment.this${capitalizeString(stripePriceFull?.recurring?.interval)}`,
                  {
                    count: stripePriceFull?.recurring?.interval_count,
                    price: localizeCurrency({
                      amountInCents: paymentIntent.amount,
                      currency: paymentIntent.currency,
                    }),
                  },
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
